import { Box, Typography, TablePagination, Skeleton } from '@mui/material';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import { getPatientOrders } from 'app/services/patient';
import { EmptyState } from 'app/shared-components/common';

function getOrderStatus(order) {
  const statusMap = {
    declined: { color: '#FF3B30', text: 'Declined' },
    done: { color: '#666666', text: 'Done' },
    requested: { color: '#2196F3', text: 'Requested' },
    ready: { color: '#4DB181', text: 'Ready' },
    processing: { color: '#4DB181', text: 'Processing' },
  };

  return statusMap[order.status] || statusMap.processing;
}

export default function PatientTab(props) {
  const { patientId, handleDrawerClose } = props;
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: { orders, pagination } = {}, isFetching } = useQuery({
    queryKey: ['patientOrders', patientId, { page, rowsPerPage }],
    queryFn: () => getPatientOrders({ patientId, page, limit: rowsPerPage }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function navigateToOrder(orderLink) {
    navigate(orderLink);
    handleDrawerClose();
  }

  return (
    <Box className="h-full flex flex-col">
      <Box className="grow overflow-y-scroll grid grid-cols-[200px_auto_200px] grid-rows-[48px_repeat(10,_min-content)] [&>*]:p-14">
        <HeaderCell>Date ordered</HeaderCell>
        <HeaderCell>Order Summary</HeaderCell>
        <HeaderCell>Status</HeaderCell>
        {!isFetching &&
          orders.length > 0 &&
          orders.map((order) => {
            const linkToOrder = order._links?.find((link) => link.rel === 'self')?.href;

            return (
              <Box
                key={order._id}
                className={`contents [&>*]:p-14 [&>*]:border-b-1 [&>*]:border-border ${
                  linkToOrder ? 'cursor-pointer hover:[&>*]:bg-neutral-100' : 'cursor-default'
                }`}
                onClick={() => linkToOrder && navigateToOrder(linkToOrder)}
              >
                <Box className="flex flex-col justify-center gap-10">
                  <Typography className="font-600 text-grey-40">
                    {moment
                      .unix(order.date_created)
                      .tz('Europe/London')
                      .format('MMM D, YYYY HH:mm')}
                  </Typography>
                </Box>
                <Box className="flex flex-col gap-8 justify-center">
                  {order.remedies?.length > 0 && (
                    <Box className="flex flex-col gap-4">
                      <Typography className="font-600 text-grey-700">
                        NHS Repeat ({order.remedies.length})
                      </Typography>
                      {order.remedies.map((remedy) => {
                        const declined = !remedy.approved || order.status === 'declined';
                        return (
                          <Box key={remedy.remedy_id} className="flex justify-between">
                            <Typography
                              className={`font-600 text-grey-40 ${declined ? 'line-through' : ''}`}
                            >
                              {remedy.medicine_name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {order.otc?.length > 0 && (
                    <Box className="flex flex-col gap-4">
                      <Typography className="font-600 text-grey-700">
                        OTC ({order.otc.length})
                      </Typography>
                      {order.otc.map((product) => {
                        const declined = !product.approved || order.status === 'declined';
                        return (
                          <Box key={product.product_id} className="flex justify-between">
                            <Typography
                              className={`font-600 text-grey-40 ${declined ? 'line-through' : ''}`}
                            >
                              {product.product_name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                <Box
                  style={{ color: getOrderStatus(order).color }}
                  className="flex items-center font-600"
                >
                  {getOrderStatus(order).text}
                </Box>
              </Box>
            );
          })}
        {!isFetching && orders.length === 0 && (
          <EmptyState title="Patient hasn't ordered" subtitle={null} />
        )}
        {isFetching && <LoadingState rows={8} />}
      </Box>
      <Box className="px-32">
        <TablePagination
          component="div"
          count={pagination?.total || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
}

const HeaderCell = ({ children }) => (
  <Box className="flex items-center sticky top-0 bg-white font-500 text-14 text-grey-40 z-10 border-b-1 border-border">
    {children}
  </Box>
);

const LoadingState = ({ rows }) => (
  <>
    {Array.from({ length: rows }, (_, i) => (
      <React.Fragment key={i}>
        <Box className="flex items-center">
          <Skeleton variant="text" width={120} />
        </Box>
        <Box>
          <Skeleton variant="text" width={120} />
          <Skeleton variant="text" width={150} />
          <Skeleton variant="text" width={200} />
        </Box>
        <Box className="flex items-center">
          <Skeleton variant="text" width={80} />
        </Box>
      </React.Fragment>
    ))}
  </>
);
